// ── PanelSection ─────────────────────────────────────────────────────────────
// Sección plegable de un panel de herramientas, al estilo de los paneles de
// Photoshop, Figma o Wix: barra de título con chevron, divisor, y el cuerpo
// debajo. Es lo que hace que un panel se lea como panel y no como un formulario
// largo — cada grupo tiene su borde y se puede cerrar lo que no se está usando.
//
// No es DetailSection: ese es para páginas de detalle, no pliega, y su
// tipografía y espaciado son de lectura. Este vive en una columna angosta al
// lado de un lienzo, se pliega, y aprieta el espaciado para que entren varios
// grupos sin scroll.
//
// El estado abierto/cerrado se recuerda por `id` en localStorage. Un panel de
// diseño que olvida qué grupos dejaste abiertos obliga a reabrirlos en cada
// recarga; los programas del rubro los recuerdan y por eso no se piensa en
// ellos.
//
// Arrancan ABIERTAS. La primera versión las abría cerradas, con el argumento
// de que así se ve el índice de lo que el panel puede hacer; visto en pantalla,
// lo que se ve es un panel que parece vacío y obliga a un clic extra antes de
// poder trabajar. Cerrar es la excepción —guardar espacio en un grupo que no
// estás usando— y la excepción la decide quien está trabajando, no el default.
//
// Lo que decida se respeta: una sección que el usuario cerró queda cerrada al
// volver, aunque el default sea abrir.
import { useState, useCallback } from 'react';
import { Icon } from '../Icon.js';

const STORAGE_PREFIX = 'evo-panel-section:';

function leerAbierto(id: string, porDefecto: boolean): boolean {
  try {
    const v = localStorage.getItem(STORAGE_PREFIX + id);
    return v === null ? porDefecto : v === '1';
  } catch {
    // Ventana privada, cookies bloqueadas, etc. El panel funciona igual.
    return porDefecto;
  }
}

export interface PanelSectionProps {
  /** Clave de persistencia. Estable en el tiempo: si cambia, se pierde el estado. */
  id: string;
  title: string;
  /** Contenido a la derecha del título — un contador, una etiqueta de origen. */
  titleRight?: React.ReactNode;
  /** Solo para la PRIMERA vez. Después manda lo que el usuario dejó. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export function PanelSection({ id, title, titleRight, defaultOpen = true, children }: PanelSectionProps) {
  const [open, setOpen] = useState(() => leerAbierto(id, defaultOpen));

  const toggle = useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_PREFIX + id, next ? '1' : '0'); } catch { /* sin persistencia */ }
      return next;
    });
  }, [id]);

  return (
    <div style={{ borderTop: '1px solid var(--evo-stroke)' }}>
      <button
        type="button"
        onClick={toggle}
        // Sin esto, apretar la barra roba el foco y la selección dentro del
        // iframe se pierde: el control que se abre actuaría sobre nada.
        onMouseDown={e => { e.preventDefault(); }}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--evo-space-2)',
          padding: 'var(--evo-space-2) 0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <span
            style={{
              display: 'flex',
              color: 'var(--evo-fg-3)',
              transform: open ? 'none' : 'rotate(-90deg)',
              transition: 'transform var(--evo-motion-fast, 120ms) ease',
            }}
          >
            <Icon name="chevron-down" size={14} />
          </span>
          <span style={{
            fontSize: 'var(--evo-fs-tiny)',
            fontFamily: 'var(--evo-font)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--evo-ls-caps)',
            color: 'var(--evo-fg-3)',
            fontWeight: 'var(--evo-fw-medium)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {title}
          </span>
        </span>
        {titleRight}
      </button>

      {open && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--evo-space-2)',
          paddingBottom: 'var(--evo-space-3)',
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

/** Fila de acciones en iconos dentro de una PanelSection. */
export function PanelToolbar({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}
